import type {Opportunity, OppSourceKind, OppStage} from "./opportunityIntelligenceDemoData";

export function formatCompactCurrency(value: number, currency: string): string {
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
            notation: 'compact',
            maximumFractionDigits: value >= 1_000_000 ? 2 : 0,
        }).format(value);
    } catch {
        return `${currency} ${value.toLocaleString('en-US')}`;
    }
}

export const STAGE_LABEL: Record<OppStage, string> = {
    detected: 'Detected',
    accepted: 'Accepted',
    in_execution: 'In execution',
    realized: 'Realized',
    abandoned: 'Abandoned',
};

/** Funnel order for sorting / progression. */
export const STAGE_ORDER: Record<OppStage, number> = {
    detected: 0,
    accepted: 1,
    in_execution: 2,
    realized: 3,
    abandoned: 4,
};

/** Lifecycle column order (kanban). */
export const STAGE_SEQUENCE: OppStage[] = ['detected', 'accepted', 'in_execution', 'realized', 'abandoned'];

/** Background / text colours for a stage pill (Mithra palette). */
export function stageColors(stage: OppStage): { bg: string; fg: string } {
    switch (stage) {
        case 'realized':
            return {bg: 'rgba(8, 165, 122, 0.12)', fg: '#08A57A'};
        case 'in_execution':
            return {bg: 'rgba(250, 169, 21, 0.14)', fg: '#cc880c'};
        case 'accepted':
            return {bg: 'rgba(64, 136, 248, 0.12)', fg: '#2f6fd1'};
        case 'detected':
            return {bg: 'rgba(68, 71, 78, 0.08)', fg: '#44474E'};
        case 'abandoned':
            return {bg: 'rgba(255, 52, 53, 0.10)', fg: '#d32f2f'};
    }
}

/** Accent colour used for a lifecycle column's top border. */
export function stageAccent(stage: OppStage): string {
    switch (stage) {
        case 'realized':
            return '#08A57A';
        case 'in_execution':
            return '#FAA915';
        case 'accepted':
            return '#478FFA';
        case 'detected':
            return '#9AA0A6';
        case 'abandoned':
            return '#c2c6cc';
    }
}

/** Dot colour for a confidence value. */
export function confidenceColor(confidence: number): string {
    if (confidence >= 80) return '#08A57A';
    if (confidence >= 60) return '#FAA915';
    return '#FF3435';
}

export const SOURCE_KIND_LABEL: Record<OppSourceKind, string> = {
    pressure: 'Budget pressure',
    agent: 'Demand Management Agent',
    should_cost: 'Should-Cost',
    benchmarking: 'Benchmarking',
    risk: 'Risk',
    finder: 'Saved finder',
    manual: 'Manual',
};

/** Accent colour for a source kind. */
export function sourceKindColor(kind: OppSourceKind): string {
    switch (kind) {
        case 'pressure':
            return '#cc880c';
        case 'agent':
            return '#8B5CF6';
        case 'should_cost':
            return '#2f6fd1';
        case 'benchmarking':
            return '#0e9488';
        case 'risk':
            return '#cc880c';
        case 'finder':
            return '#08A57A';
        case 'manual':
            return '#6b7280';
    }
}

export function sumIdentified(items: Opportunity[]): number {
    return items.reduce((sum, o) => sum + o.identifiedValue, 0);
}

export type SourceGroup = {
    label: string;
    kind: OppSourceKind;
    items: Opportunity[];
    combinedValue: number;
};

/**
 * Group opportunities by their source.
 * Ordered by number of items desc, then by combined value desc.
 */
export function groupBySource(opps: Opportunity[]): SourceGroup[] {
    const byLabel = new Map<string, SourceGroup>();
    for (const o of opps) {
        const existing = byLabel.get(o.source.label);
        if (existing) {
            existing.items.push(o);
            existing.combinedValue += o.identifiedValue;
        } else {
            byLabel.set(o.source.label, {
                label: o.source.label,
                kind: o.source.kind,
                items: [o],
                combinedValue: o.identifiedValue,
            });
        }
    }
    return [...byLabel.values()].sort((a, b) =>
        b.items.length - a.items.length || b.combinedValue - a.combinedValue);
}

export type StageGroup = {
    stage: OppStage;
    items: Opportunity[];
    total: number;
};

/** Group opportunities into lifecycle columns in funnel order. */
export function groupByStage(opps: Opportunity[]): StageGroup[] {
    return STAGE_SEQUENCE.map(stage => {
        const items = opps.filter(o => o.stage === stage);
        return {stage, items, total: sumIdentified(items)};
    }).filter(g => g.items.length > 0);
}

/** Opportunities that originated from a pressured budget line. */
export function getPressureSourced(opps: Opportunity[]): Opportunity[] {
    return opps
        .filter(o => o.source.kind === 'pressure')
        .sort((a, b) => STAGE_ORDER[a.stage] - STAGE_ORDER[b.stage]);
}
